<script setup lang="ts">
  // Caja page V2 (script setup + V2 design system), replacing the Vuetify
  // RestaurantCaja.vue. Layout ported from -pos pages/caja/index.vue: header,
  // "Efectivo en caja" hero, stats grid, session movements, actions footer.
  // Server guards (device-bound caja, cash insufficiency) are mirrored in UI
  // state but stay authoritative.
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import CajaMovementsService from '@/api/services/caja-movements.service';
  import CajasService from '@/api/services/cajas.service';
  import { PaymentMethod } from '@/enums/PaymentMethod';
  import { AlertType } from '@/interfaces/IAlert';
  import { ICaja } from '@/interfaces/ICaja';
  import { ICajaMovement } from '@/interfaces/ICajaMovement';
  import { getEffectiveTier } from '@/utils/billing-entitlements.utils';
  import { useModal } from '@/composition/use-modal';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { usePrinting } from '@/composition/use-printing';
  import { useRestaurant } from '@/composition/use-restaurant';
  import { useUi } from '@/composition/use-ui';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosV2CajaCashModal from '@/components/pos-v2/caja/PosV2CajaCashModal.vue';
  import type { IPosV2CajaCashResult } from '@/components/pos-v2/caja/PosV2CajaCashModal.vue';
  import PosV2CajaManualMovementModal from '@/components/pos-v2/caja/PosV2CajaManualMovementModal.vue';
  import type { IPosV2CajaManualMovementResult } from '@/components/pos-v2/caja/PosV2CajaManualMovementModal.vue';
  import V2CajaMovementsList from '@/components/pos-v2/caja/V2CajaMovementsList.vue';

  const restaurantStore = useRestaurantStore();
  const restaurantHelper = useRestaurant();
  const printing = usePrinting();
  const money = usePosMoney();
  const modals = useModal();
  const ui = useUi();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  //#region Module gate + caja loading

  const settingUseCajas = computed(
    () =>
      getEffectiveTier(restaurantStore.restaurant) > 0 &&
      (restaurantStore.selectedBranch?.settingUseCajas ?? false)
  );

  const selectedCajaId = computed(() => restaurantStore.caja?.id ?? null);

  const caja = ref<ICaja | null>(null);
  const isCajaLoading = ref(false);

  async function reloadCaja(): Promise<void> {
    const restaurant = restaurantStore.restaurant;
    const branch = restaurantStore.selectedBranch;
    const cajaId = selectedCajaId.value;

    if (!restaurant || !branch || !cajaId) {
      caja.value = null;
      return;
    }

    isCajaLoading.value = true;
    try {
      const loaded = await CajasService.getById(
        restaurant.id,
        branch.id,
        cajaId
      );

      // Ignore stale responses (caja switched while loading).
      if (selectedCajaId.value !== cajaId) return;

      caja.value = loaded;

      // Keep the toolbar selector's copy fresh (same as the old page).
      const storedCaja = restaurantStore.cajas?.find((c) => c.id === cajaId);
      if (storedCaja) {
        storedCaja.status = loaded.status;
        storedCaja.name = loaded.name;
      }
    } catch (e) {
      console.error(e);
      ui.serverError(e, i18nT('cajaV2.loadError'));
    } finally {
      isCajaLoading.value = false;
    }
  }

  watch(selectedCajaId, () => void reloadCaja(), { immediate: true });

  // The toolbar caja selector needs the caja list on direct navigation.
  if (!restaurantStore.cajas?.length) {
    void restaurantHelper.reloadCajas();
  }

  //#endregion

  //#region Movements (current session — the API defaults to currentStatusId)

  const movements = ref<ICajaMovement[] | null>(null);

  async function loadMovements(): Promise<void> {
    const restaurant = restaurantStore.restaurant;
    const branch = restaurantStore.selectedBranch;
    const currentCaja = caja.value;

    if (!restaurant || !branch || !currentCaja) {
      movements.value = null;
      return;
    }

    try {
      const list = await CajaMovementsService.getAll(
        restaurant.id,
        branch.id,
        currentCaja.id
      );

      if (caja.value?.id !== currentCaja.id) return;

      // Ordering (newest first) is the list's job.
      movements.value = list;
    } catch (e) {
      console.error(e);
      ui.serverError(e, i18nT('cajaV2.movementsLoadError'));
    }
  }

  // Key on the id: same-session refreshes go through refreshAll explicitly,
  // so re-assigning the caja object doesn't double-fetch movements. The
  // previous caja's rows are dropped first: none of them may be acted on
  // under the new caja's gates while the fetch is in flight.
  watch(
    () => caja.value?.id ?? null,
    () => {
      movements.value = null;
      void loadMovements();
    },
    { immediate: true }
  );

  // Exposed to the movements list so no row action targets stale rows
  // while a post-action refetch is in flight. A counter, not a flag: a
  // manual movement and a row action can finish together, and the first
  // refetch to end must not re-enable the rows the second is still fetching.
  const refreshesInFlight = ref(0);
  const isRefreshing = computed(() => refreshesInFlight.value > 0);

  async function refreshAll(): Promise<void> {
    refreshesInFlight.value++;
    try {
      await reloadCaja();
      await loadMovements();
    } finally {
      refreshesInFlight.value--;
    }
  }

  //#endregion

  //#region Display helpers

  const statusConfig = computed(() => {
    if (!caja.value) return null;
    return caja.value.status === 'open'
      ? {
          label: i18nT('cajaV2.statusOpen'),
          color: 'success' as const,
          icon: 'check-circle-outline',
        }
      : {
          label: i18nT('cajaV2.statusClosed'),
          color: 'neutral' as const,
          icon: 'minus-circle-outline',
        };
  });

  const isOpen = computed(() => caja.value?.status === 'open');

  /** Server guard mirror: device-bound cajas are POS-device-operated only. */
  const isDeviceBound = computed(() => caja.value?.posDeviceId != null);

  const canManualMove = computed(() => isOpen.value && !isDeviceBound.value);

  const canOpenClose = computed(() => !isDeviceBound.value);

  const STAT_LABEL_KEYS: Record<string, string> = {
    cashIn: 'cajaV2.statCashIn',
    card: 'cajaV2.statCard',
    online: 'cajaV2.statOnline',
    banktransfer: 'cajaV2.statBankTransfer',
    qr: 'cajaV2.statQr',
    invitation: 'cajaV2.statInvitation',
    employee: 'cajaV2.statEmployee',
    customer: 'cajaV2.statCustomer',
    club: 'cajaV2.statClub',
  };

  const secondaryStats = computed(() => {
    const c = caja.value;
    if (!c) return [];
    return Object.keys(STAT_LABEL_KEYS).map((key) => ({
      key,
      labelKey: STAT_LABEL_KEYS[key],
      value: Number(c[key as keyof ICaja] ?? 0),
    }));
  });

  //#endregion

  //#region Ceremonies (open / close / print)

  const isChangingStatus = ref(false);

  async function askCash(
    title: string,
    message: string,
    confirmLabel: string,
    initial: number | null
  ): Promise<number | null> {
    const result = await modals.showDynamicDialog<IPosV2CajaCashResult | null>(
      PosV2CajaCashModal,
      {
        title,
        message,
        confirmLabel,
        initial,
        currencySymbol: restaurantStore.currency?.symbol ?? '$',
      },
      'caja-cash',
      { clickToClose: false }
    );
    return result?.amount ?? null;
  }

  async function postStatusMovement(
    type: 'open' | 'close',
    amount: number
  ): Promise<void> {
    await CajaMovementsService.create({
      restaurantId: restaurantStore.restaurant.id,
      branchId: restaurantStore.selectedBranch.id,
      cajaId: caja.value!.id,
      type,
      amount,
    });
  }

  async function handleOpen(): Promise<void> {
    if (!caja.value || isChangingStatus.value) return;

    const amount = await askCash(
      i18nT('cajaV2.openTitle'),
      i18nT('cajaV2.openMessage'),
      i18nT('cajaV2.open'),
      caja.value.cash ?? null
    );
    if (amount == null) return;

    isChangingStatus.value = true;
    try {
      await postStatusMovement('open', amount);
      await refreshAll();
      ui.showToast(AlertType.Success, i18nT('cajaV2.openedToast'));
    } catch (e) {
      console.error(e);
      ui.serverError(e, i18nT('cajaV2.statusChangeError'));
    } finally {
      isChangingStatus.value = false;
    }
  }

  async function handleClose(): Promise<void> {
    if (!caja.value || isChangingStatus.value) return;

    isChangingStatus.value = true;
    try {
      // Guard first: blocking payments abort, carry-overs need a confirm.
      const preview = await CajasService.closePreview(
        restaurantStore.restaurant.id,
        restaurantStore.selectedBranch.id,
        caja.value.id
      );

      if (preview.blockingCount > 0) {
        ui.showToast(AlertType.Error, i18nT('cajaV2.closeBlocking'));
        return;
      }

      if (preview.carryOverCount > 0) {
        const confirmed = await modals.confirm({
          title: i18nT('cajaV2.closeTitle'),
          message: i18nT('cajaV2.closeCarryOverConfirm', {
            count: preview.carryOverCount,
          }),
        });
        if (!confirmed) return;
      }

      const amount = await askCash(
        i18nT('cajaV2.closeTitle'),
        i18nT('cajaV2.closeMessage'),
        i18nT('cajaV2.close'),
        null
      );
      if (amount == null) return;

      await postStatusMovement('close', amount);
      await refreshAll();
      ui.showToast(AlertType.Success, i18nT('cajaV2.closedToast'));

      // Auto-print the Z resume non-blockingly: a printing failure must
      // never make the close look failed.
      void printResume();
    } catch (e) {
      console.error(e);
      ui.serverError(e, i18nT('cajaV2.statusChangeError'));
    } finally {
      isChangingStatus.value = false;
    }
  }

  async function printResume(): Promise<void> {
    if (!caja.value) return;

    try {
      const resume = await CajasService.getResume(
        restaurantStore.restaurant.id,
        restaurantStore.selectedBranch.id,
        caja.value.id
      );
      await printing.printCajaResume(resume);
    } catch (e) {
      console.error(e);
      ui.serverError(e, i18nT('cajaV2.printError'));
    }
  }

  /** Server decides X vs Z from caja status; the label just reflects it. */
  const printLabel = computed(() =>
    isOpen.value ? i18nT('cajaV2.printPartial') : i18nT('cajaV2.printFinal')
  );

  //#endregion

  //#region Manual movements

  async function createManualMovement(
    type: 'inflow' | 'outflow'
  ): Promise<void> {
    if (!caja.value || !canManualMove.value) return;

    const result = await modals.showDynamicDialog<IPosV2CajaManualMovementResult | null>(
      PosV2CajaManualMovementModal,
      {
        type,
        availableCash: type === 'outflow' ? caja.value.cash ?? null : null,
        currencySymbol: restaurantStore.currency?.symbol ?? '$',
      },
      'caja-manual-movement',
      { clickToClose: false }
    );
    if (!result) return;

    try {
      await CajaMovementsService.create({
        restaurantId: restaurantStore.restaurant.id,
        branchId: restaurantStore.selectedBranch.id,
        cajaId: caja.value.id,
        type,
        paymentMethod: PaymentMethod.Cash,
        amount: result.amount,
        ...(result.description ? { description: result.description } : {}),
      });

      await refreshAll();
      ui.showToast(AlertType.Success, i18nT('cajaV2.movementCreated'));
    } catch (e) {
      console.error(e);
      ui.serverError(e, i18nT('cajaV2.movementCreateError'));
    }
  }

  //#endregion
</script>

<template>
  <div class="page-caja-v2">
    <div class="page-card">
      <!-- Header -->
      <header class="caja-header">
        <h1 class="page-title">{{ $t('cajaV2.title') }}</h1>
        <template v-if="caja">
          <span class="caja-name">{{ caja.name }}</span>
          <PosUiBadge
            v-if="statusConfig"
            :color="statusConfig.color"
            :icon="statusConfig.icon"
            size="sm"
          >
            {{ statusConfig.label }}
          </PosUiBadge>
          <span v-if="caja.currentStatusId" class="session-id">
            #{{ caja.currentStatusId }}
          </span>
        </template>
      </header>

      <!-- Module disabled -->
      <div v-if="!settingUseCajas" class="page-state">
        <div class="state-content">
          <PosUiIcon name="cash-register" class="state-icon" />
          <h3 class="state-title">{{ $t('cajaV2.moduleDisabledTitle') }}</h3>
          <p class="state-text">{{ $t('cajaV2.moduleDisabledText') }}</p>
        </div>
      </div>

      <!-- No caja selected -->
      <div v-else-if="!caja && !isCajaLoading" class="page-state">
        <div class="state-content">
          <PosUiIcon name="cash-register" class="state-icon" />
          <h3 class="state-title">{{ $t('cajaV2.noCajaTitle') }}</h3>
          <p class="state-text">{{ $t('cajaV2.noCajaText') }}</p>
        </div>
      </div>

      <!-- Loading -->
      <div v-else-if="!caja" class="page-state">
        <div class="state-content">
          <PosUiIcon name="loading" class="state-icon spinning" />
          <p class="state-text">{{ $t('cajaV2.loading') }}</p>
        </div>
      </div>

      <!-- Main content -->
      <template v-else>
        <div class="caja-body">
          <!-- Device-bound notice -->
          <div v-if="isDeviceBound" class="context-notice">
            <PosUiIcon name="tablet" class="notice-icon" />
            <span>{{ $t('cajaV2.deviceBoundNotice') }}</span>
          </div>

          <!-- Amounts -->
          <section class="amounts-section">
            <div class="cash-hero">
              <span class="cash-label">{{ $t('cajaV2.cashHero') }}</span>
              <span class="cash-value">
                {{ money.formatComputedMoney(caja.cash ?? 0) }}
              </span>
            </div>

            <div class="stats-row">
              <div
                v-for="stat in secondaryStats"
                :key="stat.key"
                class="stat-cell"
                :class="{ 'stat-cell--zero': stat.value === 0 }"
              >
                <span class="stat-label">{{ $t(stat.labelKey) }}</span>
                <span class="stat-value">
                  {{ money.formatComputedMoney(stat.value) }}
                </span>
              </div>
            </div>
          </section>

          <!-- Movements -->
          <section class="movements-section">
            <h2 class="section-title">{{ $t('cajaV2.movementsTitle') }}</h2>

            <div v-if="!movements" class="empty-movements">
              <PosUiIcon name="loading" class="spinning" />
              <p>{{ $t('cajaV2.movementsLoading') }}</p>
            </div>

            <div v-else-if="!movements.length" class="empty-movements">
              <p>{{ $t('cajaV2.movementsEmpty') }}</p>
            </div>

            <V2CajaMovementsList
              v-else
              :movements="movements"
              :caja="caja"
              :refreshing="isRefreshing"
              @changed="refreshAll"
            />
          </section>
        </div>

        <!-- Actions footer -->
        <footer class="caja-footer">
          <div class="footer-left">
            <PosUiButton
              color="error"
              variant="solid"
              size="lg"
              icon="minus-circle-outline"
              class="action-btn"
              :disabled="!canManualMove"
              @click="createManualMovement('outflow')"
            >
              {{ $t('cajaV2.withdraw') }}
            </PosUiButton>
            <PosUiButton
              color="success"
              variant="solid"
              size="lg"
              icon="plus-circle-outline"
              class="action-btn"
              :disabled="!canManualMove"
              @click="createManualMovement('inflow')"
            >
              {{ $t('cajaV2.deposit') }}
            </PosUiButton>
          </div>

          <div class="footer-right">
            <PosUiButton
              color="neutral"
              variant="outline"
              size="lg"
              icon="printer"
              class="action-btn"
              @click="printResume"
            >
              {{ printLabel }}
            </PosUiButton>

            <PosUiButton
              v-if="canOpenClose && !isOpen"
              color="success"
              variant="soft"
              size="lg"
              icon="door-open"
              class="action-btn"
              :disabled="isChangingStatus"
              @click="handleOpen"
            >
              {{ $t('cajaV2.open') }}
            </PosUiButton>
            <PosUiButton
              v-else-if="canOpenClose && isOpen"
              color="warning"
              variant="solid"
              size="lg"
              icon="door-closed"
              class="action-btn"
              :disabled="isChangingStatus"
              @click="handleClose"
            >
              {{ $t('cajaV2.closeCaja') }}
            </PosUiButton>
          </div>
        </footer>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .page-caja-v2 {
    @apply tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-bg-canvas tw-px-4 tw-py-4 md:tw-px-6;

    // Content-page archetype (V2 design system): a centered card with a
    // horizontal buffer to the window/side-menu edges on large screens.
    .page-card {
      @apply tw-mx-auto tw-flex tw-min-h-0 tw-w-full tw-max-w-7xl tw-flex-1 tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white;

      .caja-header {
        @apply tw-flex tw-flex-none tw-items-center tw-gap-2 tw-border-b tw-border-neutral-200 tw-bg-white tw-px-4 tw-py-2;

        .page-title {
          @apply tw-text-lg tw-font-bold tw-text-neutral-900;
        }

        .caja-name {
          @apply tw-border-l tw-border-neutral-300 tw-pl-2 tw-text-sm tw-text-neutral-600;
        }

        .session-id {
          @apply tw-text-xs tw-tabular-nums tw-text-neutral-400;
        }
      }

      .page-state {
        @apply tw-flex tw-flex-1 tw-items-center tw-justify-center tw-p-4;

        .state-content {
          @apply tw-flex tw-flex-col tw-items-center tw-gap-3 tw-text-center;

          .state-icon {
            @apply tw-text-4xl tw-text-neutral-300;
          }

          .state-title {
            @apply tw-text-lg tw-font-semibold tw-text-neutral-900;
          }

          .state-text {
            @apply tw-max-w-md tw-text-sm tw-text-neutral-500;
          }
        }
      }

      .spinning {
        @apply tw-animate-spin;
      }

      .caja-body {
        @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-y-auto;

        .context-notice {
          @apply tw-flex tw-items-center tw-gap-2 tw-border-b tw-border-sky-100 tw-bg-sky-50 tw-px-4 tw-py-2 tw-text-sm tw-text-sky-800;

          .notice-icon {
            @apply tw-flex-none tw-text-base;
          }
        }

        .amounts-section {
          @apply tw-flex-none tw-border-b tw-border-neutral-200 tw-bg-white tw-p-3;

          .cash-hero {
            @apply tw-mb-3 tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-border tw-border-emerald-200 tw-bg-gradient-to-r tw-from-emerald-50 tw-to-green-50 tw-px-4 tw-py-3;

            .cash-label {
              @apply tw-text-sm tw-font-medium tw-text-emerald-800;
            }

            .cash-value {
              @apply tw-text-2xl tw-font-bold tw-tabular-nums tw-text-emerald-700;
            }
          }

          .stats-row {
            @apply tw-grid tw-grid-cols-2 tw-gap-px tw-overflow-hidden tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-neutral-200 sm:tw-grid-cols-3 lg:tw-grid-cols-3 xl:tw-grid-cols-3;

            .stat-cell {
              @apply tw-flex tw-items-center tw-justify-between tw-gap-2 tw-bg-white tw-px-3 tw-py-2;

              &--zero {
                @apply tw-opacity-50;
              }

              .stat-label {
                @apply tw-truncate tw-text-xs tw-text-neutral-500;
              }

              .stat-value {
                @apply tw-flex-none tw-text-sm tw-font-semibold tw-tabular-nums tw-text-neutral-900;
              }
            }
          }
        }

        .movements-section {
          @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-canvas tw-p-3;

          .section-title {
            @apply tw-mb-2 tw-flex-none tw-text-sm tw-font-semibold tw-text-neutral-900;
          }

          .empty-movements {
            @apply tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-py-8 tw-text-sm tw-text-neutral-400;
          }
        }
      }

      .caja-footer {
        @apply tw-flex tw-flex-none tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-border-t tw-border-neutral-200 tw-bg-white tw-px-3 tw-py-2;

        .footer-left,
        .footer-right {
          @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;
        }

        :deep(.action-btn) {
          @apply tw-min-h-[48px] tw-whitespace-nowrap;
        }
      }
    }
  }
</style>
